import { message } from 'antd'
import { CurrentCompanyStore } from '../stores/CurrentCompanyStore'
import { POSStore } from '../stores/POSStore'
import { IUberEatsStoreEnvironment, UberEatsStore } from '../stores/UberEatsStore'
import { IUberEatsChime, createUberEatsChime } from './uberEatsChime'
import { LocalStorage } from './LocalStorage'
import { createUberEatsOrderService, registerUberEatsMock } from './uberEatsOrderService'

export interface IUberEatsPOSStores {
  posStore: Pick<POSStore, 'uberEatsOrderAlertEnabled' | 'device'>
  currentCompanyStore: Pick<CurrentCompanyStore, 'canUseUberEats'>
}

const queueScope = ({ device }: Pick<POSStore, 'device'>) =>
  `${device.companyId}:${device.locationId}:${device.id}`

/**
 * The flags resolve after the POS shell renders, so they are read through getters on every access
 * and never copied at construction.
 */
const createEnvironment = (
  { posStore, currentCompanyStore }: IUberEatsPOSStores,
  chime: IUberEatsChime,
): IUberEatsStoreEnvironment => ({
  isAlertDevice: () => !!currentCompanyStore.canUseUberEats && posStore.uberEatsOrderAlertEnabled,
  loadAlertTiming: LocalStorage.getUberEatsAlertTiming,
  saveAlertTiming: LocalStorage.setUberEatsAlertTiming,
  loadNewOrderPause: LocalStorage.getUberEatsNewOrderPause,
  saveNewOrderPause: LocalStorage.setUberEatsNewOrderPause,
  loadIncomingQueue: () => LocalStorage.getUberEatsIncomingQueue(queueScope(posStore)),
  saveIncomingQueue: (queue) => LocalStorage.setUberEatsIncomingQueue(queueScope(posStore), queue),
  playChime: chime.play,
  primeChime: chime.prime,
  notifySuccess: (text) => {
    message.success(text)
  },
  notifyError: (text) => {
    message.error(text)
  },
  now: Date.now,
})

export const createUberEatsPOSStore = (
  stores: IUberEatsPOSStores,
  chime: IUberEatsChime = createUberEatsChime(),
) => new UberEatsStore(createUberEatsOrderService(), createEnvironment(stores, chime))

export const startUberEatsPOSSession = (stores: IUberEatsPOSStores, chime?: IUberEatsChime) => {
  const store = createUberEatsPOSStore(stores, chime)
  store.restoreTicker()
  const unregisterMock = registerUberEatsMock(store, store.orderService)

  return {
    store,
    stop: () => {
      store.dispose()
      unregisterMock()
    },
  }
}
